import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { closetItems } from '../data/mockData';

const Studio = () => {
  const [activeCategory, setActiveCategory] = useState('top');
  const [canvasItems, setCanvasItems] = useState({
    top: null,
    bottom: null,
    outerwear: null,
    shoes: null,
    accessory: null
  });

  const categories = ['top', 'bottom', 'outerwear', 'shoes', 'accessory'];
  const availableItems = closetItems.filter(item => item.type === activeCategory);

  const handleSelect = (item) => {
    setCanvasItems(prev => ({ ...prev, [item.type]: item }));
  };

  const handleRemove = (type) => {
    setCanvasItems(prev => ({ ...prev, [type]: null }));
  };

  const isCanvasEmpty = Object.values(canvasItems).every(item => item === null);

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col lg:flex-row gap-6">
      
      {/* Canvas Area (Right on desktop, Top on mobile) */}
      <div className="lg:w-1/2 flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h1 className="font-editorial text-2xl">Outfit Canvas</h1>
          <button 
            className="btn-primary text-xs py-1.5"
            disabled={isCanvasEmpty}
            style={{ opacity: isCanvasEmpty ? 0.5 : 1 }}
          >
            SAVE LOOK
          </button>
        </div>
        
        <div className="flex-1 bg-white border border-pearl-200 relative p-8 flex items-center justify-center min-h-[400px]">
          {isCanvasEmpty ? (
            <div className="text-center text-graphite-400">
              <SafeIcon name="Layers" className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="font-mono text-sm">SELECT ITEMS FROM THE ARCHIVE TO BUILD A LOOK</p>
            </div>
          ) : (
            <div className="w-full max-w-sm relative">
              {/* Layout logic for selected items on canvas */}
              <AnimatePresence>
                {canvasItems.outerwear && (
                  <motion.div initial={{ opacity:0, scale:0.9 }} animate={{ opacity:1, scale:1 }} exit={{ opacity:0 }} className="absolute -top-12 -left-12 w-48 z-20 group">
                     <img src={canvasItems.outerwear.image} className="w-full drop-shadow-xl mix-blend-multiply" alt="outerwear"/>
                     <button onClick={() => handleRemove('outerwear')} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><SafeIcon name="X" className="w-3 h-3"/></button>
                  </motion.div>
                )}
                {canvasItems.top && (
                  <motion.div initial={{ opacity:0, y:-20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }} className="w-48 mx-auto relative z-10 group">
                     <img src={canvasItems.top.image} className="w-full drop-shadow-md mix-blend-multiply" alt="top"/>
                     <button onClick={() => handleRemove('top')} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><SafeIcon name="X" className="w-3 h-3"/></button>
                  </motion.div>
                )}
                {canvasItems.bottom && (
                  <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }} className="w-48 mx-auto -mt-8 relative z-0 group">
                     <img src={canvasItems.bottom.image} className="w-full drop-shadow-md mix-blend-multiply" alt="bottom"/>
                     <button onClick={() => handleRemove('bottom')} className="absolute top-4 right-0 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><SafeIcon name="X" className="w-3 h-3"/></button>
                  </motion.div>
                )}
                {canvasItems.shoes && (
                  <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }} className="w-32 mx-auto mt-4 relative z-0 group">
                     <img src={canvasItems.shoes.image} className="w-full drop-shadow-md mix-blend-multiply" alt="shoes"/>
                     <button onClick={() => handleRemove('shoes')} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><SafeIcon name="X" className="w-3 h-3"/></button>
                  </motion.div>
                )}
                {canvasItems.accessory && (
                  <motion.div initial={{ opacity:0, x:20 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0 }} className="absolute top-1/4 right-0 w-24 z-30 group">
                     <img src={canvasItems.accessory.image} className="w-full drop-shadow-md mix-blend-multiply" alt="accessory"/>
                     <button onClick={() => handleRemove('accessory')} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><SafeIcon name="X" className="w-3 h-3"/></button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {/* Selector Area (Left on desktop, Bottom on mobile) */}
      <div className="lg:w-1/2 flex flex-col h-full">
        <div className="flex border-b border-pearl-200 mb-4 overflow-x-auto scrollbar-hide">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-3 text-xs font-mono uppercase tracking-wider whitespace-nowrap border-b-2 transition-colors ${
                activeCategory === cat 
                  ? 'border-graphite-900 text-graphite-900' 
                  : 'border-transparent text-graphite-400 hover:text-graphite-600'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto pr-2 grid grid-cols-2 sm:grid-cols-3 gap-4">
          {availableItems.map(item => (
            <motion.div
              key={item.id}
              whileHover={{ scale: 1.02 }}
              onClick={() => handleSelect(item)}
              className={`cursor-pointer border p-2 transition-colors ${
                canvasItems[activeCategory]?.id === item.id 
                  ? 'border-runway-500 bg-runway-500/5' 
                  : 'border-pearl-200 bg-white hover:border-graphite-300'
              }`}
            >
              <div className="aspect-square bg-pearl-50 mb-2 p-2">
                 <img src={item.image} alt={item.name} className="w-full h-full object-cover mix-blend-multiply" />
              </div>
              <p className="text-xs font-semibold truncate">{item.brand}</p>
              <p className="text-[10px] text-graphite-500 truncate">{item.name}</p>
            </motion.div>
          ))}
          {availableItems.length === 0 && (
            <div className="col-span-full py-8 text-center text-graphite-400 text-sm font-mono">
              NO ASSETS FOUND IN THIS CATEGORY
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default Studio;